import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { AiFillWarning, AiFillExclamationCircle } from "react-icons/ai";
import { selectCurrentUser, updateCredentials } from "../../features/auth/authSlice";
import { useCurrentUserQuery } from "../../features/chat/chatSlice";
import { CountryList } from "../../components/Dashboard/giftcards/CountryList";
import { BrandList } from "../../components/Dashboard/giftcards/BrandList";
import { ProductList } from "../../components/Dashboard/giftcards/ProductList";
import { PurchaseForm } from "../../components/Dashboard/giftcards/PurchaseForm";
import { GiftcardTransactionHistory } from "../../components/Dashboard/giftcards/GiftcardTransactionHistory";
import { useGetGiftcardStatusQuery, GiftcardProduct } from "../../features/giftcards/giftcardsSlice";
import { FaArrowLeft } from "react-icons/fa";

type Step = "countries" | "brands" | "products" | "purchase";

interface SelectedData {
  countryCode?: string;
  countryName?: string;
  brandId?: number;
  brandName?: string;
  product?: GiftcardProduct;
}

export const BuyGiftcardsPage = () => {
  const dispatch = useDispatch();
  const user = useSelector(selectCurrentUser);
  const { data: currentUser } = useCurrentUserQuery();

  const [step, setStep] = useState<Step>("countries");
  const [selected, setSelected] = useState<SelectedData>({});

  const { data: status, isLoading: statusLoading } = useGetGiftcardStatusQuery();

  // Sync KYC status when it changes
  useEffect(() => {
    if (currentUser && user && user?.isKYCVerified !== currentUser?.isKYCVerified) {
      dispatch(updateCredentials({ user: currentUser }));
    }
  }, [currentUser, user, dispatch]);

  const handleCountrySelect = (countryCode: string, countryName: string) => {
    setSelected({ countryCode, countryName });
    setStep("brands");
  };

  const handleBrandSelect = (brandId: number, brandName: string) => {
    setSelected((prev) => ({ ...prev, brandId, brandName }));
    setStep("products");
  };

  const handleProductSelect = (product: GiftcardProduct) => {
    setSelected((prev) => ({ ...prev, product }));
    setStep("purchase");
  };

  const handleBack = () => {
    switch (step) {
      case "brands":
        setStep("countries");
        setSelected({});
        break;
      case "products":
        setStep("brands");
        setSelected((prev) => ({ countryCode: prev.countryCode, countryName: prev.countryName }));
        break;
      case "purchase":
        setStep("products");
        setSelected((prev) => ({
          countryCode: prev.countryCode,
          countryName: prev.countryName,
          brandId: prev.brandId,
          brandName: prev.brandName,
          product: undefined,
        }));
        break;
    }
  };

  const handleComplete = () => {
    setStep("countries");
    setSelected({});
  };

  const getStepTitle = () => {
    switch (step) {
      case "countries":
        return "Select Country";
      case "brands":
        return `Brands in ${selected.countryName}`;
      case "products":
        return `${selected.brandName} Products`;
      case "purchase":
        return `Buy ${selected.product?.productName}`;
    }
  };

  // KYC gate — block non-verified users from purchasing
  if (!user?.isKYCVerified) {
    return (
      <div className="font-dashboard pt-4 pb-12 px-4 min-h-screen">
        <div className="max-w-2xl mx-auto">
          <div className="bg-gray-900 rounded-xl shadow-lg p-8 border border-gray-700">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-yellow-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <AiFillWarning className="w-10 h-10 text-yellow-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-4">KYC Verification Required</h1>
              <p className="text-gray-300 mb-6">
                To purchase gift cards, you need to complete your KYC verification first.
                This helps us ensure the security of your account and comply with regulatory requirements.
              </p>
            </div>

            <div className="bg-yellow-900/30 border border-yellow-600 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <AiFillExclamationCircle className="w-5 h-5 text-yellow-400 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-yellow-300">Why is KYC required?</h4>
                  <ul className="text-sm text-yellow-200 mt-2 space-y-1">
                    <li>• Protects your account from unauthorized access</li>
                    <li>• Complies with financial regulations</li>
                    <li>• Enables higher transaction limits</li>
                    <li>• Provides access to all platform features</li>
                  </ul>
                </div>
              </div>
            </div>

            <Link to="/app/account/kyc">
              <button
                type="button"
                className="w-full bg-primary text-white py-3 px-6 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
              >
                Complete KYC Verification
              </button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (statusLoading) {
    return (
      <div className="font-dashboard pt-4 pb-12 px-4 min-h-screen flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  if (!status?.isEnabled) {
    return (
      <div className="font-dashboard pt-4 pb-12 px-4 min-h-screen">
        <div className="container-lg">
          <div className="bg-black px-4 py-5 rounded-lg text-center">
            <h3 className="text-primary text-xl font-medium mb-4">Gift Cards</h3>
            <p className="text-gray-400 mb-4">
              {status?.supportMessage || "Gift card service is currently unavailable. Please check back later."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="font-dashboard pt-4 pb-12 px-4 min-h-screen">
      <div className="container-lg">
        <div className="bg-black px-4 py-5 rounded-lg">
          <div className="flex items-center mb-6">
            {step !== "countries" && (
              <button
                onClick={handleBack}
                className="mr-3 text-gray-400 hover:text-white transition-colors"
              >
                <FaArrowLeft />
              </button>
            )}
            <h3 className="text-primary text-xl font-medium">{getStepTitle()}</h3>
          </div>

          {step === "countries" && (
            <CountryList onSelect={handleCountrySelect} />
          )}

          {step === "brands" && selected.countryCode && (
            <BrandList
              countryCode={selected.countryCode}
              onSelect={handleBrandSelect}
            />
          )}

          {step === "products" && selected.countryCode && selected.brandId && (
            <ProductList
              countryCode={selected.countryCode}
              brandId={selected.brandId}
              onSelect={handleProductSelect}
            />
          )}

          {step === "purchase" && selected.product && selected.countryCode && (
            <PurchaseForm
              product={selected.product}
              countryCode={selected.countryCode}
              onComplete={handleComplete}
              onBack={handleBack}
            />
          )}
        </div>

        <GiftcardTransactionHistory />
      </div>
    </div>
  );
};
